/**
 * Channels List Page — shows all configured channel integrations for the tenant.
 *
 * Entry point: /channels
 * From here users can create a new Teams Bot (wizard), Slack, WhatsApp, Telegram.
 */

import { useNavigate } from 'react-router-dom';
import { Plus, Bot, Trash2, ExternalLink, ChevronDown } from 'lucide-react';
import { Button, Card, Badge } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTenant } from '@/contexts/TenantContext';
import { useChannels, useDeleteChannel } from '@/features/channels';
import type { ChannelIntegration, ChannelType } from '@/features/channels/types';
import { toast } from 'sonner';

// =============================================================================
// Helpers
// =============================================================================

const CHANNEL_LABELS: Record<ChannelType, string> = {
  teams: 'Microsoft Teams',
  whatsapp: 'WhatsApp Business',
  slack: 'Slack',
  telegram: 'Telegram',
};

const STATUS_VARIANT: Record<string, 'secondary' | 'outline' | 'destructive' | 'default'> = {
  active: 'secondary',
  pending_verification: 'outline',
  paused: 'outline',
  circuit_open: 'destructive',
  error: 'destructive',
};

function ChannelRow({ channel, onDelete }: { channel: ChannelIntegration; onDelete: (id: string) => void }) {
  const navigate = useNavigate();

  return (
    <div
      className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer"
      onClick={() => navigate(`/channels/${channel.id}`)}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30">
          <Bot className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-white">{channel.display_name ?? channel.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {CHANNEL_LABELS[channel.channel_type] ?? channel.channel_type}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <Badge variant={STATUS_VARIANT[channel.status] ?? 'default'}>
          {channel.status}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(channel.id)}
          title="Delete channel"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Page
// =============================================================================

export function ChannelsListPage() {
  const navigate = useNavigate();
  const { currentTenant } = useTenant();
  const { data, isLoading } = useChannels();
  const deleteMutation = useDeleteChannel();

  const handleDelete = async (channelId: string) => {
    if (!confirm('Delete this channel integration? This cannot be undone.')) return;
    try {
      await deleteMutation.mutateAsync(channelId);
      toast.success('Channel deleted');
    } catch {
      toast.error('Failed to delete channel');
    }
  };

  if (!currentTenant) {
    return (
      <div className="p-8 text-center text-gray-500">Select a tenant first.</div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Channel Integrations
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Connect your AI agents to messaging platforms.
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="default" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                Add Channel
                <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/channels/new/teams')}>
                Microsoft Teams
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/channels/new/slack')}>
                Slack
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/channels/new/whatsapp')}>
                WhatsApp Business
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/channels/new/telegram')}>
                Telegram
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Channels List */}
      <Card>
        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : !data?.items?.length ? (
          <div className="p-12 text-center">
            <Bot className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No channel integrations yet.</p>
            <div className="mt-3 flex justify-center">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Add your first channel
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => navigate('/channels/new/teams')}>
                    Microsoft Teams
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/channels/new/slack')}>
                    Slack
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/channels/new/whatsapp')}>
                    WhatsApp Business
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/channels/new/telegram')}>
                    Telegram
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        ) : (
          <div>
            {data.items.map((ch) => (
              <ChannelRow key={ch.id} channel={ch} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </Card>

      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800">
        <ExternalLink className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-xs text-blue-700 dark:text-blue-300">
          Supported channels: Microsoft Teams, WhatsApp Business, Slack, Telegram.
          More coming soon.
        </p>
      </div>
    </div>
  );
}
